import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Checkbox,
  Divider,
  Flex,
  FlexProps,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  InputProps,
  InputRightAddon,
  Select,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { copyText } from '@pelicargo/ui';
import { Filters as FilterType } from '@pelicargo/utils';
import { Filters } from '@pelicargo/utils';
import { FilterExpression, FilterExpressionSnippet } from '@prisma/client';
import { intersection, uniq } from 'lodash';
import { ChangeEvent, MutableRefObject, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import React from 'react';
import { useForm } from 'react-hook-form';
import { HiOutlineClipboardCopy } from 'react-icons/hi';
import {
  HiCheck,
  HiMiniVariable,
  HiNewspaper,
  HiOutlineEquals,
  HiPlus,
  HiQuestionMarkCircle,
  HiSquare3Stack3D,
  HiTrash,
  HiXMark,
} from 'react-icons/hi2';

import { trpc, TrpcOutputs } from '../config/trpc';
import { SimpleForm } from './SimpleForm';
import { SimpleInput } from './SimpleInput';

const {
  CheckType,
  DataType,
  groupLogic,
  groupOperationBorders,
  groupOperations,
  valueLogic,
  dataSource,
  enumValues,
  defaultValues,
  variableSourceInfo,
  variableSource,
  scopeSources,
  relations,
} = Filters;

type Config = {
  sources: (keyof typeof Filters.dataSource)[];
  enabledVariables: Partial<Record<keyof FilterType.VariableSources, Record<string, string>>>;
  // Todo, at some point: References don't go into subobjects.
  availableReferences: Partial<Record<FilterType.SCOPES, FilterExpression[]>>;
  toast: ReturnType<typeof useToast>;
  snippets: FilterExpressionSnippet[];
  availableGlobals: string[];
};

type Trace = TrpcOutputs['testFilter']['trace'];

export type Condition = {
  check: () => FilterType.CompiledType;
  render: (style: FlexProps, trace: Trace, path: string) => React.JSX.Element;
  getConfig: () => Config;
};

export type ComparisonInputArgs = {
  comparisonType: FilterType.DataType;
  args: FilterType.ValueArgs;
  style: InputProps;
  possibleVariables: () => [keyof FilterType.VariableSources, string][];
  inputRef: React.RefObject<any>;
};

const copyFilter = (config: Config, check: () => FilterType.CompiledType, type: FilterType.CheckType) => {
  copyText(
    JSON.stringify(
      {
        meta: {
          sources: config.sources,
          type,
          isFilter: true,
        },
        filter: check(),
      },
      undefined,
      2,
    ),
  );
  config.toast({
    colorScheme: 'green',
    title: 'Copied Successfully!',
  });
};

const EvalIcon = ({ trace, path }: { trace: Trace; path: string }) => {
  if (!trace?.[path]) {
    return <></>;
  }

  const text = `${trace[path].input} <> ${trace[path].comparison}`;
  if (uniq(trace[path].result).length >= 2) {
    return <Icon as={HiQuestionMarkCircle} color="yellow" alignSelf="center" title={text} />;
  }

  return (
    <>
      <Show if={trace[path]?.result?.[0]}>
        <Icon as={HiCheck} color="green" alignSelf="center" title={text} />
      </Show>
      <Show if={!trace[path]?.result?.[0]}>
        <Icon as={HiXMark} color="#F00" alignSelf="center" title={text} />
      </Show>
    </>
  );
};

const sourceSort = (a: string, b: string) => a.replace('$', '').localeCompare(b.replace('$', ''));

// Used to render the input of a field
const ComparisonInput = ({ comparisonType, args, style, inputRef, possibleVariables }: ComparisonInputArgs) => {
  // A literal not being defined () is what determines whether or not to use a variable
  if (args.comparison?.literal === undefined) {
    const possible = possibleVariables();
    // Since it works off of indexes, you gotta find the set index
    const selected = possible.findIndex(
      ([source, variable]) => source === args.comparison.source && args.comparison.variable === variable,
    );
    return (
      <Select
        defaultValue={selected}
        onChange={(event) => {
          const selection: (typeof possible)[0] = possible[event.target.value];
          args.comparison.source = selection[0];
          args.comparison.variable = selection[1];
        }}
      >
        {possible.map(([source, value], i) => (
          <option key={i} value={i}>
            {source} -&gt; {value}
          </option>
        ))}
      </Select>
    );
  }

  // This is used in both an input and a select, and typings get weird. We can at least get some code completion...
  const onChange = (event: ChangeEvent<HTMLSelectElement | any>) =>
    (args['comparison'].literal = event.target.value as string);

  switch (comparisonType) {
    case DataType.Number:
      return (
        <Input
          type="number"
          defaultValue={args?.comparison?.literal}
          onChange={onChange}
          placeholder="0, 10, -5, 4.15..."
          step="0.01"
          {...style}
          ref={inputRef}
        />
      );
    case DataType.Enum: {
      const values = args.source === 'global' ? enumValues[args.value] : enumValues[`${args.source}.${args.value}`];

      return (
        <Select onChange={onChange} defaultValue={args?.comparison?.literal} ref={inputRef}>
          {values.map((value, i) => (
            <option key={i} value={value}>
              {value}
            </option>
          ))}
        </Select>
      );
    }
    case DataType.SubObject:
    case DataType.ListSubObject:
    case DataType.Reference:
    case DataType.Boolean:
      return (
        <Select onChange={onChange} defaultValue={args?.comparison?.literal} ref={inputRef}>
          <option value="true">True</option>
          <option value="false">False</option>
        </Select>
      );
    case DataType.String:
      return (
        <Input onChange={onChange} defaultValue={args?.comparison?.literal} type="text" {...style} ref={inputRef} />
      );
    case DataType.Date:
      return <></>;
    case DataType.ListEnum:
    case DataType.ListNumber:
    case DataType.ListString:
    case DataType.ListBoolean:
      return <Text>[Only supported as var]</Text>;
    case DataType.Unsupported:
      return <Text>[Unsupported]</Text>;
    default: {
      const comprehensiveCheck: never = comparisonType;
      return comprehensiveCheck;
    }
  }
};

// Renders a single comparison
const ValueCondition = (redraw: () => void, config: Config, base?: FilterType.CompiledValueType): Condition => {
  const defaultValue = Object.keys(dataSource?.[config.sources?.[0]] || {})?.[0];
  const args: FilterType.ValueArgs = base?.args ?? {
    source: config.sources[0],
    value: defaultValue,
    comparison: {
      literal: defaultValues?.[dataSource?.[config.sources?.[0]]?.[defaultValue]]?.comparison,
    },
  };

  const inputRef = React.createRef<FilterType.ValueArgs['comparison']>();
  let subCondition: ReturnType<typeof GroupCondition>;

  if (base?.args?.comparison?.subConditions) {
    subCondition = GroupCondition(
      redraw,
      {
        sources: [relations[args['source']][args['value']]],
        enabledVariables: config?.enabledVariables,
        availableReferences: {},
        toast: config.toast,
        snippets: config.snippets,
        availableGlobals: config.availableGlobals,
      },
      base?.args?.comparison?.subConditions,
    );
  }

  const sourceValues = () => {
    switch (args['source']) {
      case 'filter':
        return Object.keys(config.availableReferences ?? {}).reduce((agg, scope: FilterType.SCOPES) => {
          for (const filter of config.availableReferences[scope]) {
            agg[`${scope} - ${filter.identifier}`] = DataType.Boolean;
          }
          return agg;
        }, {});
      case 'snippet':
        return Filters.matchingSnippets(config.snippets, config.sources).reduce(
          (agg, snip) => ({
            ...agg,
            [snip.identifier]: DataType.Boolean,
          }),
          {},
        );
      case 'global':
        return config.availableGlobals.reduce((agg, glob) => {
          const [source, value] = glob.split('.');
          agg[glob] = dataSource[source][value];

          return agg;
        }, {});
    }

    return dataSource[args['source']];
  };
  const sourceType = () => sourceValues()?.[args?.['value']] as FilterType.DataType;

  let func = base?.func ?? defaultValues[sourceType()]?.func;

  const resetComparison = () => {
    args.comparison = { literal: defaultValues[sourceType()]?.comparison };

    if (inputRef.current) {
      // Reset to the default enum
      if (sourceType() === DataType.Enum) {
        // At this point it's assured to be a value, so type as such
        const { source, value } = args as FilterType.ValueArgs;
        const defaultEnum: string = enumValues[`${source}.${value}`][0];
        inputRef.current.literal = defaultEnum;
        args.comparison.literal = defaultEnum;
      } else {
        inputRef.current.literal = args.comparison.literal;
      }
    }
  };

  // Runs on changing the source to reset to default
  const onSourceChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const extra = ['snippet', 'filter', 'global'];
    // We control the values...
    args['source'] = event.target.value as (typeof args)['source'];

    if (extra.includes(event.target.value)) {
      args['value'] = Object.keys(sourceValues()).toSorted(sourceSort)[0];
      resetComparison();
      func = defaultValues[sourceType()].func;
      redraw();
      return;
    }

    if (!sourceType()) {
      args['value'] = Object.keys(sourceValues())[0];
      resetComparison();
    }
    func = defaultValues[sourceType()].func;
    redraw();
  };

  const onValueChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const oldType = sourceType();
    args['value'] = event.target.value;
    if (oldType !== dataSource[args['source']][event.target.value]) {
      func = defaultValues[sourceType()].func;
      resetComparison();
      if (sourceType() === DataType.SubObject || sourceType() === DataType.ListSubObject) {
        subCondition = GroupCondition(redraw, {
          sources: [relations[args['source']][args['value']]],
          enabledVariables: config?.enabledVariables,
          availableReferences: {},
          toast: config.toast,
          snippets: config.snippets,
          availableGlobals: config.availableGlobals,
        });
      } else {
        subCondition = undefined;
      }
    }

    redraw();
  };

  const onFuncChange = (event: ChangeEvent<HTMLSelectElement>) => {
    func = event.target.value as typeof func;
    redraw();
  };

  const formatSnakeCase = (s: string) => s.replace(/_/g, ' ');

  const renderOption = (value: string) => (
    <option key={value} value={value}>
      {formatSnakeCase(value).replace('$', '↳')}
    </option>
  );

  const possibleVariables = () => {
    const matches = [];
    const allSources = [...Object.keys(config.enabledVariables), ...config.sources];
    const type = (valueLogic?.[sourceType()]?.[func] as FilterType.LogicFunction<any, any>)?.cmpArg;

    for (const source of allSources) {
      for (const variable in dataSource[source]) {
        if (type === dataSource[source][variable]) {
          if (type === DataType.Enum || type == DataType.ListEnum) {
            const variableEnums = enumValues[`${source}.${variable}`] ?? [];
            const targetEnums = enumValues[`${args['source']}.${args['value']}`] ?? [];

            // We want to allow subsets to be compared against supersets
            if (targetEnums?.length && intersection(variableEnums, targetEnums)?.length === targetEnums?.length) {
              matches.push([source, variable]);
            }
          } else {
            matches.push([source, variable]);
          }
        }
      }
    }
    return matches;
  };

  const toggleVariable = () => {
    const possible = possibleVariables();
    if (args.comparison.literal !== undefined) {
      args.comparison.literal = undefined;
      args.comparison.source = possible[0][0];
      args.comparison.variable = possible[0][1];
    } else {
      args.comparison.literal = defaultValues[sourceType()].comparison;
    }
    redraw();
  };

  const extendedSources = () => {
    const sources: (keyof FilterType.VariableSources | keyof typeof dataSource)[] = [...config.sources];
    for (const variable of Object.keys(config.enabledVariables)) {
      sources.push(variable as keyof FilterType.VariableSources);
    }
    return sources;
  };

  const check = () => {
    if (subCondition) {
      args['comparison']['subConditions'] = subCondition.check() as Filters.CompiledGroupType;
    }
    return {
      type: CheckType.data,
      func,
      args,
    } as const;
  };

  return {
    getConfig: () => config,
    check,
    render: (_, trace: Trace, path: string) => {
      const logicFunction: FilterType.LogicFunction<FilterType.DataType, FilterType.DataType> =
        valueLogic?.[sourceType()]?.[func];

      return (
        <Flex width="full" flexDir="column">
          <Flex gap="0.75em" width="full">
            <EvalIcon trace={trace} path={path} />
            <Select variant="inline" defaultValue={args['source']} onChange={onSourceChange} textTransform="capitalize">
              {extendedSources().map((op) => (
                <option key={op} value={op}>
                  {op in variableSource ? '𝑥' : ''} {op}'s
                </option>
              ))}
              {Object.keys(config.availableReferences ?? {})?.length ? (
                <option value="filter">external filter</option>
              ) : (
                <></>
              )}
              <Show if={!!Filters.matchingSnippets(config.snippets, config.sources)?.length}>
                <option value="snippet">snippet</option>
              </Show>
              <option value="global">global</option>
            </Select>{' '}
            <Select variant="inline" defaultValue={args['value']} onChange={onValueChange} textTransform="capitalize">
              {Object.keys(sourceValues() ?? {})
                .toSorted(sourceSort)
                .map(renderOption)}
            </Select>{' '}
            <Select variant="inline" value={func} onChange={onFuncChange} textTransform="capitalize">
              {Object.keys(valueLogic?.[sourceType()] || {}).map(renderOption)}
            </Select>
            <InputGroup>
              <Show if={!logicFunction?.static}>
                <ComparisonInput
                  args={args}
                  comparisonType={logicFunction?.cmpArg}
                  possibleVariables={possibleVariables}
                  style={{
                    flexShrink: 1,
                  }}
                  inputRef={inputRef}
                />
                <InputRightAddon cursor="pointer" borderStyle="outset" px="1" onClick={toggleVariable}>
                  <Show if={!!args.comparison?.literal}>
                    <HiMiniVariable />
                  </Show>
                  <Show if={!args.comparison?.literal}>
                    <HiOutlineEquals />
                  </Show>
                </InputRightAddon>
              </Show>
            </InputGroup>
            <IconButton
              icon={<HiOutlineClipboardCopy />}
              aria-label="Copy Value"
              onClick={() => copyFilter(config, check, Filters.CheckType.data)}
            />
          </Flex>
          <Show if={!!subCondition}>
            {subCondition?.render(
              {
                ml: '.5em',
              },
              trace,
              `${path}.0`,
            )}
          </Show>
        </Flex>
      );
    },
  };
};

export const GroupCondition = (
  redraw: () => void,
  config: Config,
  base?: FilterType.CompiledGroupType,
  depth: number = 0,
): Condition => {
  let condition = base?.func ?? 'all';
  let label = base?.label ?? '';
  const subConditions: Condition[] =
    base?.args?.map((arg) => {
      switch (arg.type) {
        case CheckType.data:
          return ValueCondition(redraw, config, arg);
        case CheckType.group:
          return GroupCondition(redraw, config, arg, depth + 1);
        default:
          // This should never be reached in actual use, this is here for us if we extend it somehow or break the editor
          throw Error('Unknown CondType - Conditional editor is misconfigured, please report!');
      }
    }) ?? [];

  const addValueCondition = () => {
    subConditions.push(ValueCondition(redraw, config));
    redraw();
  };

  const addGroupCondition = () => {
    subConditions.push(GroupCondition(redraw, config, null, depth + 1));
    redraw();
  };

  const renderSubcondition = (cond: Condition, i: number, trace: Trace, path: string) => {
    // Prevent issue where deletion would cause a rendering issue. Not a pleasant fix, but it works
    if (!cond['_uid']) {
      cond['_uid'] = crypto.randomUUID();
    }
    return (
      <Flex key={cond['_uid']} justifyContent="space-between">
        {cond.render({}, trace, `${path}.${i}`)}
        <IconButton
          aria-label="Remove condition"
          title="Remove condition"
          icon={<HiTrash />}
          colorScheme="red"
          onClick={() => {
            subConditions.splice(i, 1);
            redraw();
          }}
          mx={4}
        />
      </Flex>
    );
  };

  const onOperationChange = (event: ChangeEvent<HTMLSelectElement>) => {
    condition = event.target.value as typeof condition;

    redraw();
  };

  const renderGroupOperation = (op) => (
    <option key={op} value={op}>
      {groupOperations[op]}
    </option>
  );

  const check = () =>
    ({
      type: CheckType.group,
      func: condition,
      label,
      args: subConditions.map((cond) => cond.check()),
    }) as const;

  const addFromClipboard = async () => {
    const text = await navigator.clipboard.readText();
    let json: any;
    try {
      json = JSON.parse(text);
      if (!json?.meta?.isFilter) {
        throw new Error();
      }
    } catch (e) {
      return config.toast({
        colorScheme: 'red',
        title: 'Error',
        description: "Clipboard doesn't seem to have a valid group condition",
      });
    }
    const filter = json?.filter;

    if (intersection(config.sources, json.meta.sources)?.length !== json.meta.sources.length) {
      return config.toast({
        colorScheme: 'red',
        title: 'Error',
        description: 'Filter cannot be pasted in this section. ',
      });
    }
    if (json.meta.type === CheckType.group) {
      subConditions.push(GroupCondition(redraw, config, filter, depth + 1));
    } else {
      subConditions.push(ValueCondition(redraw, config, filter));
    }
    redraw();
  };

  return {
    getConfig: () => config,
    check,
    render: (args, trace, path) => {
      return (
        <Flex
          direction="column"
          gap={5}
          borderLeft={groupOperationBorders[condition]}
          borderRadius="0.25em"
          p="0.2em"
          width="100%"
          background={`rgba(75, 74, 103, ${depth * 0.1})`}
          {...args}
        >
          <Accordion defaultIndex={depth === 0 ? [0] : []} allowMultiple>
            <AccordionItem>
              <Flex gap="2">
                <AccordionButton w={2}>
                  <AccordionIcon />
                </AccordionButton>
                <EvalIcon trace={trace} path={path} />
                <Select fontWeight="bold" defaultValue={condition} onChange={onOperationChange}>
                  {Object.keys(groupLogic).map(renderGroupOperation)}
                </Select>
                <InputGroup>
                  <InputLeftAddon>
                    <HiNewspaper />
                  </InputLeftAddon>
                  <Input
                    defaultValue={label}
                    onChange={(event) => {
                      label = event.target.value;
                    }}
                    placeholder="Optional label"
                  />
                </InputGroup>
                <IconButton
                  icon={<HiOutlineClipboardCopy />}
                  aria-label="Copy Group"
                  onClick={() => copyFilter(config, check, Filters.CheckType.group)}
                />
              </Flex>
              <AccordionPanel>
                <Flex ml="0.5em" direction="column" gap="0.5em">
                  {subConditions.map((cond, i) => renderSubcondition(cond, i, trace, path))}

                  <Flex gap={5} justifyContent="flex-start">
                    <Button leftIcon={<HiPlus />} aria-label="add value" onClick={addValueCondition}>
                      Add value condition
                    </Button>
                    <Button leftIcon={<HiSquare3Stack3D />} aria-label="add condition" onClick={addGroupCondition}>
                      Add group condition
                    </Button>
                    <Button leftIcon={<HiOutlineClipboardCopy />} onClick={addFromClipboard}>
                      Add from clipboard
                    </Button>
                  </Flex>
                </Flex>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Flex>
      );
    },
  };
};

type BaseEditorProps = {
  rootRef: MutableRefObject<Condition>;
  // Base is always a group
  base?: FilterType.CompiledGroupType;
  baseVariables?: Config['enabledVariables'];
  //Allows toggling variables in high-performance operations
  allowVariables?: boolean;
};

type ScopedEditorProps = BaseEditorProps & {
  scope: FilterType.SCOPES;
  sources?: undefined;
};

type SourceOnlyEditorProps = BaseEditorProps & {
  scope?: undefined;
  sources: Filters.DataSources[];
};

type EditorProps = ScopedEditorProps | SourceOnlyEditorProps;

export const FilterEditor = ({
  rootRef,
  scope = undefined,
  sources = undefined,
  base = null,
  baseVariables = null,
  allowVariables = true,
}: EditorProps) => {
  // All this does is provide a function to trigger a re-render the editor, due to the statefulness of this.
  // yell at me later
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const selectedSources = useMemo(() => sources ?? [...scopeSources[scope]], [scope, sources]);

  const { data: references, isLoading: referencesLoading } = trpc.getReferenceableFilters.useQuery(
    {
      scope,
    },
    {
      enabled: !sources,
    },
  );

  const { data: snippets, isLoading: snippetsLoading } = trpc.getSnippets.useQuery({
    sources: [],
  });

  const toast = useToast();

  const condition = useRef<ReturnType<typeof GroupCondition>>(null);
  const config = useRef<Config>({
    sources: selectedSources,
    enabledVariables: baseVariables ?? {},
    availableReferences: references ?? {},
    snippets: [],
    toast,
    availableGlobals: Filters.buildAvailableGlobals(selectedSources),
  });

  // Simple optimization, prevents creating a new GC object on every render
  if (!condition.current) {
    condition.current = GroupCondition(forceUpdate, config.current, base);
  }

  const replaceFromClipboard = async () => {
    const text = await navigator.clipboard.readText();
    let json: any;
    try {
      json = JSON.parse(text);
      if (!json?.meta?.isFilter) {
        throw new Error();
      }
      if (json?.filter?.type !== 'group') {
        return toast({
          colorScheme: 'red',
          title: 'Error',
          description: 'Must be a group condition',
        });
      }
    } catch (e) {
      return toast({
        colorScheme: 'red',
        title: 'Error',
        description: "Clipboard doesn't seem to have a valid group condition",
      });
    }
    const filter = json?.filter;

    if (intersection(selectedSources, json.meta.sources)?.length !== json.meta.sources.length) {
      return toast({
        colorScheme: 'red',
        title: 'Error',
        description: 'Filter cannot be pasted in this section. ',
      });
    }
    condition.current = GroupCondition(forceUpdate, config.current, filter);
    forceUpdate();
  };

  rootRef.current = condition.current;

  config.current.availableReferences = references;
  config.current.snippets = snippets;
  useEffect(() => {
    forceUpdate();
  }, [references, snippets]);

  const validVariables = useMemo(
    () =>
      Object.keys(variableSourceInfo).filter((key) => {
        const source = variableSourceInfo[key];
        for (const requirement of source.prerequisites) {
          if (!selectedSources.includes(requirement)) {
            return false;
          }
        }
        return true;
      }),
    [selectedSources],
  );

  const toggleVariable = useCallback((variable: keyof FilterType.VariableSources) => {
    const exists = variable in config.current.enabledVariables;
    if (exists) {
      delete config.current.enabledVariables[variable];
    } else {
      config.current.enabledVariables[variable] = Object.entries(variableSourceInfo[variable].discriminators).reduce(
        (agg, [name, { options }]) => ({ ...agg, [name]: options[0] }),
        {},
      );
    }
    forceUpdate();
  }, []);

  useEffect(() => {
    config.current.availableReferences = references;
    forceUpdate();
  }, [references]);

  const { mutateAsync: submitTest } = trpc.testFilter.useMutation();

  const debugForm = useForm({
    mode: 'onSubmit',
  });

  const [trace, setTrace] = useState<Trace>();
  const [debugSuccess, setDebugSuccess] = useState<boolean>(undefined);

  const submitDebug = async (inputs) => {
    const { result, trace: newTrace } = await submitTest({
      ...inputs,
      variables: config.current.enabledVariables,
      filter: condition.current.check(),
    });
    setTrace(newTrace);
    setDebugSuccess(result);
  };

  if (((referencesLoading || config.current.availableReferences !== references) && !sources) || snippetsLoading) {
    return <></>;
  }

  return (
    <Flex direction="row" height="100%" gap={2}>
      <Flex w={validVariables.length ? '75%' : 'full'} overflowY="auto">
        {rootRef.current.render({}, trace, '')}
      </Flex>
      <Flex w="24%" direction="column" gap={2}>
        <Button onClick={replaceFromClipboard} leftIcon={'⚠️' as any /* hey, it works... */} colorScheme="yellow">
          Replace From Clipboard
        </Button>
        <Show if={!!validVariables.length && allowVariables}>
          <Flex direction="column" height="50%">
            {validVariables.map((sourceName: keyof FilterType.VariableSources) => {
              const source = variableSourceInfo[sourceName];
              const enabled = !!config.current.enabledVariables[sourceName];

              return (
                <Flex
                  key={source.name}
                  flexDir="column"
                  border="1px solid"
                  borderColor="gray.200"
                  p={1}
                  color={enabled ? undefined : 'gray.600'}
                >
                  <Flex justifyContent="space-between" w="full">
                    {source.name} <Checkbox isChecked={enabled} onChange={() => toggleVariable(sourceName)} />
                  </Flex>
                  <Flex fontSize="2xs">{source.description}</Flex>
                  <Flex fontSize="2xs" flexDir="column">
                    {Object.keys(source.discriminators).map((name) => {
                      const { label, options } = source.discriminators[name];

                      return (
                        <Flex flexDir="column" key={label} mt="1">
                          <Flex fontWeight="bold">{label}</Flex>
                          <Select
                            size="xs"
                            disabled={!enabled}
                            defaultValue={config?.current?.enabledVariables?.[sourceName]?.[name]}
                            onChange={(e) => {
                              // whew
                              config.current.enabledVariables[sourceName][name] = e.target.value;
                            }}
                          >
                            {options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </Select>
                        </Flex>
                      );
                    })}
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
          <Divider />
        </Show>

        <SimpleForm {...debugForm}>
          <Stack>
            <Text fontWeight="bold" textAlign="center">
              Debugger
            </Text>

            {selectedSources.map((source) => {
              return <SimpleInput key={source} name={source} label={`${source} ID`} />;
            })}
            <Button
              type="button"
              onClick={debugForm.handleSubmit(submitDebug)}
              colorScheme={debugSuccess !== undefined ? (debugSuccess ? 'green' : 'red') : undefined}
            >
              Test
            </Button>
          </Stack>
        </SimpleForm>
      </Flex>
    </Flex>
  );
};
